import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  ciSearchQuery,
  ignorePending,
  mergePending,
  needsClass,
  pendingShellQuery,
  pendingTableQuery,
  promotePending,
  savePendingPolicy,
  type CiOption,
  type PendingColumn,
  type PendingRow,
} from '../../api/admPending'
import { ApiError } from '../../api/client'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField } from './AdmFilterBar'
import { SettingsCheck } from '../../ui/SettingsParts'

/**
 * ADM -> Review queue, din `views/version-3/softsee/adm/pending/index.php`.
 *
 * NIMIC NU SE PROMOVEAZA DE LA SINE PE ECRANUL ASTA. O adresa descoperita e o
 * banuiala pana cand cineva confirma ca e o masina care merita urmarita, iar un
 * CMDB plin de banuieli e mai rau decat unul cu goluri: golurile se vad.
 *
 * SE DESENEAZA NUMAI COLOANELE PE CARE LE-A TRIMIS SERVERUL. Fabricantul si
 * amprenta sunt goale pe fiecare rand pana pleaca agentul de scanare; o coloana
 * de celule goale langa singurul control care conteaza aici ar arata ca ecranul
 * si-a pierdut datele, deci nu se deseneaza deloc pana n-are ce pune in ea.
 */

const STYLES = ['css/adm.css']

function esc(value: unknown): string {
  const box = document.createElement('div')
  box.textContent = String(value ?? '')
  return box.innerHTML
}

/** Indiciile, ca pastile - `admPills` din adm.js. */
function pills(items: string[], more: number): string {
  if (!items || items.length === 0) return ''
  const shown = items.map((item) => `<span class="adm-chip">${esc(item)}</span>`).join(' ')
  return more > 0 ? `${shown} <span class="adm-muted">+${more}</span>` : shown
}

/** Etichetele capetelor, ca in macheta. */
const HEADS: Record<PendingColumn, { label: string; width?: number }> = {
  name: { label: 'Name' },
  ip: { label: 'Address' },
  mac: { label: 'MAC' },
  manufacturer: { label: 'Made by' },
  os: { label: 'Operating system' },
  fingerprint: { label: 'What answered', width: 220 },
  company: { label: 'Customer' },
  match: { label: 'Possible match' },
  first_seen: { label: 'First seen' },
  decide: { label: 'Decide', width: 290 },
}

/** Coloanele pe care se poate ordona - aceeasi lista ca `DbalPendingSource::SORTABLE`. */
const SORTABLE: PendingColumn[] = ['name', 'ip', 'mac', 'manufacturer', 'os', 'company', 'match', 'first_seen']

function queueColumns(keys: PendingColumn[], canWrite: boolean): TableColumn[] {
  return keys.map((key, index) => {
    const head = HEADS[key]
    const base = { index, title: head.label, orderable: SORTABLE.includes(key), visible: true, width: head.width }

    switch (key) {
      case 'ip':
        return { ...base, className: 'adm-nowrap', html: (r) => esc((r as unknown as PendingRow).ip) }
      case 'mac':
        return { ...base, className: 'adm-nowrap', html: (r) => esc((r as unknown as PendingRow).mac) }
      case 'manufacturer':
        /* Citit din adresa fizica de langa el, si de-aia sta acolo: raspunde la
           "ce e lucrul asta" inainte sa fie nevoie sa se deschida ceva. */
        return { ...base, html: (r) => esc((r as unknown as PendingRow).manufacturer_name) }
      case 'os':
        return { ...base, html: (r) => esc((r as unknown as PendingRow).os) }
      case 'fingerprint':
        return {
          ...base,
          orderable: false,
          html: (r) => {
            /* Indiciile sunt concluzia scanerului, in cuvinte; porturile de
               dedesubt sunt socoteala din spatele ei, stinse, fiindca cineva le
               citeste doar cand concluzia pare gresita. */
            const row = r as unknown as PendingRow
            let html = pills(row.hints, row.hints_more)
            if (row.ports && row.ports.length > 0) {
              html += `<div class="adm-muted adm-nowrap" style="font-size:11px">${esc(row.ports.join(', '))}${
                row.ports_more > 0 ? ` +${row.ports_more}` : ''
              }</div>`
            }
            return html
          },
        }
      case 'company':
        return { ...base, html: (r) => esc((r as unknown as PendingRow).company_name) }
      case 'match':
        return {
          ...base,
          html: (r) => {
            const name = (r as unknown as PendingRow).matched_name
            return name ? esc(name) : '<span class="adm-muted">—</span>'
          },
        }
      case 'first_seen':
        return { ...base, className: 'adm-nowrap adm-muted', html: (r) => esc((r as unknown as PendingRow).first_seen) }
      case 'decide':
        return {
          ...base,
          orderable: false,
          className: 'text-right adm-nowrap',
          html: (r) => {
            const row = r as unknown as PendingRow
            if (row.state !== 1) {
              /* Utilizatorul 0 e platforma: cine rasfoieste istoria trebuie sa
                 vada dintr-o privire care intrari le-a privit cineva si care
                 le-au asezat regulile. */
              const by = row.decided_by_user_id > 0 ? '' : ' <span class="adm-muted">(automatic)</span>'
              return `<span class="adm-pill">${esc(row.state_label)}</span>${by}`
            }
            if (!canWrite) return ''
            /* Cu numele, nu doar cu iconita. Trei iconite una langa alta sunt o
               ghicitoare, iar asta e chiar ecranul al carui rost intreg e
               hotararea - deci hotararea se scrie in cuvinte. */
            /* Fara tip de la agent, clasa e ghicita pe server: omul o vede, si o
               confirma sau o schimba in fereastra care se deschide la clic. */
            const guess =
              row.class_from === 'guess'
                ? row.kind
                  ? ` - the scan suggests ${esc(row.kind)}; you choose the CI class`
                  : ' - the scan could not tell what this is; you choose the CI class'
                : ''
            return (
              `<button type="button" class="btn btn-success btn-xs admPromote" data-id="${row.ID}"` +
              ` title="Create a configuration item for this address${guess}"><i class="fa fa-plus"></i> Add to CMDB</button>` +
              ` <button type="button" class="btn btn-info btn-xs admMerge" data-id="${row.ID}"` +
              ` data-name="${esc(row.device_name || row.ip)}"` +
              ' title="This address is an item the CMDB already has"><i class="fa fa-link"></i> Already have it</button>' +
              ` <button type="button" class="btn btn-warning btn-xs admIgnore" data-id="${row.ID}"` +
              ' title="Never offer this address again"><i class="fa fa-ban"></i> Ignore</button>'
            )
          },
        }
      default:
        return {
          ...base,
          /* Numele scurt, cu FQDN-ul dedesubt (wks02 / wks02.cecdemo.local). */
          html: (r) => {
            const row = r as unknown as PendingRow
            return esc(row.device_name) + (row.fqdn ? `<div class="adm-muted" style="font-size:11px">${esc(row.fqdn)}</div>` : '')
          },
        }
    }
  })
}

export function PendingQueuePage() {
  const appShell = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, appShell.data?.version ?? '')

  const [state, setStateFilter] = useState('1')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [merging, setMerging] = useState<{ id: number; name: string } | null>(null)
  const [term, setTerm] = useState('')
  const [chosen, setChosen] = useState(0)
  /* Fereastra "Add to CMDB as…": aparatul fara tip, clasa ghicita preselectata. */
  const [classing, setClassing] = useState<{
    id: number
    name: string
    kind: string
    classes: CiOption[]
    chosen: number
    message: string
  } | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const shell = useQuery(pendingShellQuery())
  const [table, setTable] = useTableState('admPending', {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })

  const keys = shell.data?.columns ?? []
  const canWrite = shell.data?.canWrite ?? false

  /* Ordinea se cere pe NUMELE coloanei: indicii se muta cu cele doua coloane de
     dovezi, iar cele doua jumatati ar fi ajuns sa inteleaga altceva prin
     "coloana a patra". */
  const orderKey = keys[table.order.column] ?? 'first_seen'

  const rows = useQuery({
    ...pendingTableQuery({
      draw: 1,
      start: table.start,
      length: table.length,
      orderColumn: orderKey,
      orderDir: table.order.dir,
      search: debounced,
      state,
    }),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(() => queueColumns(keys, canWrite), [keys, canWrite])
  const found = useQuery(ciSearchQuery(term))

  const refresh = () => {
    void rows.refetch()
    void shell.refetch()
  }

  /* Serverul n-a gasit clasa (sau a refuzat-o pe cea aleasa): fereastra se
     deschide - sau ramane deschisa - cu lista si cu motivul lui. */
  const askForClass = (id: number, error: unknown): boolean => {
    const need = error instanceof ApiError ? needsClass(error.details) : null
    if (!need) return false
    const row = (rows.data?.data ?? []).find((r) => r.ID === id)
    setClassing((open) => ({
      id,
      name: open?.name ?? (row ? row.device_name || row.ip : ''),
      kind: need.kind || open?.kind || row?.kind || '',
      classes: need.classes,
      chosen: need.class_id || 0,
      message: need.message,
    }))
    return true
  }

  const decide = async (what: 'promote' | 'ignore', id: number) => {
    if (what === 'promote') {
      const row = (rows.data?.data ?? []).find((r) => r.ID === id)
      const classes = shell.data?.classes ?? []
      /* Fara tip de la agent, clasa e o banuiala: o alege omul, cu felul
         ghicit gata preselectat. Cu tip, merge direct, ca pana acum. */
      if (row && row.class_from === 'guess' && classes.length > 0) {
        setClassing({ id, name: row.device_name || row.ip, kind: row.kind, classes, chosen: row.class_id, message: '' })
        return
      }
    }
    try {
      const answer = what === 'promote' ? await promotePending(id) : await ignorePending(id)
      toast(answer.message, 'Review queue', 'success')
      refresh()
    } catch (error) {
      if (what === 'promote' && askForClass(id, error)) return
      toast((error as Error).message, 'Review queue', 'error')
    }
  }

  const doPromoteAs = async () => {
    if (!classing || classing.chosen < 1) return
    try {
      const answer = await promotePending(classing.id, classing.chosen)
      toast(answer.message, 'Review queue', 'success')
      setClassing(null)
      refresh()
    } catch (error) {
      if (askForClass(classing.id, error)) return
      toast((error as Error).message, 'Review queue', 'error')
    }
  }

  /* Cele trei butoane sunt scrise in HTML-ul celulei, ca in portal: clicul se
     prinde o data, pe tabel. */
  useEffect(() => {
    const host = document.getElementById('admPendingTable')
    if (!host) return
    const onClick = (event: Event) => {
      const target = event.target as HTMLElement
      const promote = target.closest('.admPromote')
      const ignore = target.closest('.admIgnore')
      const merge = target.closest('.admMerge')
      if (promote) return void decide('promote', Number(promote.getAttribute('data-id')))
      if (ignore) return void decide('ignore', Number(ignore.getAttribute('data-id')))
      if (merge) {
        setTerm('')
        setChosen(0)
        setMerging({ id: Number(merge.getAttribute('data-id')), name: merge.getAttribute('data-name') ?? '' })
      }
    }
    host.addEventListener('click', onClick)
    return () => host.removeEventListener('click', onClick)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.data, shell.data])

  const doMerge = async () => {
    if (!merging || chosen < 1) return
    try {
      const answer = await mergePending(merging.id, chosen)
      toast(answer.message, 'Review queue', 'success')
      setMerging(null)
      refresh()
    } catch (error) {
      toast((error as Error).message, 'Review queue', 'error')
    }
  }

  const setPolicy = async (autoMerge: boolean, autoPromote: boolean) => {
    try {
      await savePendingPolicy(autoMerge, autoPromote)
      void shell.refetch()
    } catch (error) {
      toast((error as Error).message, 'Review queue', 'error')
    }
  }

  const waiting = shell.data?.waiting ?? 0
  const policy = shell.data?.policy ?? { auto_merge: false, auto_promote: false }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="yellow"
          icon="fa-inbox"
          title="Review queue"
          note="addresses discovery found that are not configuration items yet. Each is yours to add, to match against something the CMDB already has, or to ignore"
          stats={[{ value: waiting.toLocaleString('en-US'), label: 'waiting', tone: waiting > 0 ? 'yellow' : 'grey' }]}
        />

        <AdmFilterBar>
          <AdmField label="Search" htmlFor="admPendingSearch" wide>
            <input
              type="text"
              className="form-control input-sm"
              id="admPendingSearch"
              placeholder="name, address or maker"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </AdmField>
          <AdmField label="Decision" htmlFor="admPendingState">
            <select className="form-control input-sm" id="admPendingState" value={state} onChange={(e) => setStateFilter(e.target.value)}>
              {(shell.data?.states ?? []).map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </AdmField>
        </AdmFilterBar>

        {/* Ce hotaraste platforma singura. Aici, nu intr-un ecran de setari,
            fiindca aici se vede urmarea: cu amandoua pornite, coada tine numai
            ce nu se putea hotari fara sa fie privit. */}
        <div className="adm-card adm-tone-yellow" style={{ marginBottom: 16 }}>
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-robot"></i> What arrives here
            </h5>
            <span className="adm-card-note">an address is only queued when there is something to decide</span>
          </div>

          <div className="adm-policy">
            <SettingsCheck
              className="admPolicy"
              id="autoMerge"
              checked={policy.auto_merge}
              disabled={!canWrite}
              onChange={(on) => void setPolicy(on, policy.auto_promote)}
              label={
                <>
                  <strong>Record a known machine automatically.</strong> An address whose hardware address already belongs
                  to a configuration item is recorded against it. It is the same machine &mdash; there is nothing to weigh
                  up.
                </>
              }
            />
          </div>

          <div className="adm-policy">
            <SettingsCheck
              className="admPolicy"
              id="autoPromote"
              checked={policy.auto_promote}
              disabled={!canWrite}
              onChange={(on) => void setPolicy(policy.auto_merge, on)}
              label={
                <>
                  <strong>Add an unknown machine automatically.</strong> An address of a type you track, carrying a
                  hardware address, becomes a configuration item. Anything without one &mdash; a phantom, a gateway,
                  something that answered a ping once &mdash; still waits here.
                </>
              }
            />
          </div>
        </div>

        <div id="admPendingTable">
          <DataTable
            id="admPending"
            columns={columns}
            rows={(rows.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={rows.data?.recordsTotal ?? 0}
            fetching={rows.isFetching}
            error={rows.isError ? (rows.error as Error).message : null}
            onRetry={() => void rows.refetch()}
            state={table}
            onState={setTable}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            emptyHint="Nothing is waiting for a decision."
          />
        </div>
      </div>

      {/* Alegatorul de clasa, pentru aparatul fara tip. Felul ghicit pe server
          vine preselectat; omul il confirma sau alege alta clasa. */}
      <Modal
        id="admClassModal"
        open={classing !== null}
        onClose={() => setClassing(null)}
        title="Add to CMDB as…"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setClassing(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success"
              id="admClassConfirm"
              disabled={!classing || classing.chosen < 1}
              onClick={() => void doPromoteAs()}
            >
              Add to CMDB
            </button>
          </>
        }
      >
        <p className="adm-muted">{classing ? classing.name : ''}</p>
        {classing && classing.message ? <div className="alert alert-warning">{classing.message}</div> : null}
        {classing && !classing.message ? (
          <p>
            {classing.kind ? (
              <>
                The agent did not report a device type. The scan suggests <strong>{classing.kind}</strong> &mdash; check
                the CI class before adding it.
              </>
            ) : (
              <>The agent did not report a device type, and the scan could not tell what this is. Choose its CI class.</>
            )}
          </p>
        ) : null}
        {classing && classing.classes.length > 0 ? (
          <div className="form-group">
            <label htmlFor="admClassSelect">CI class</label>
            <select
              className="form-control"
              id="admClassSelect"
              value={classing.chosen}
              onChange={(e) => setClassing({ ...classing, chosen: Number(e.target.value) })}
            >
              <option value={0}>Choose a class…</option>
              {classing.classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </Modal>

      {/* Alegatorul de imbinare. Cauta elemente NUMAI in acelasi client -
          imbinarea peste clienti ar muta o adresa in inventarul altui client, si
          asta nu se desface editand un rand dupa aceea. Ingustarea o face
          serverul, din jeton; ecranul nici n-o poate cere altfel. */}
      <Modal
        id="admMergeModal"
        open={merging !== null}
        onClose={() => setMerging(null)}
        title="This is the same item as…"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setMerging(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-info" id="admMergeConfirm" disabled={chosen < 1} onClick={() => void doMerge()}>
              Record
            </button>
          </>
        }
      >
        <p className="adm-muted">{merging ? merging.name : ''}</p>
        <div className="form-group">
          <label htmlFor="admMergeSearch">Configuration item</label>
          <input
            type="text"
            className="form-control"
            id="admMergeSearch"
            placeholder="type at least two letters"
            autoComplete="off"
            value={term}
            onChange={(e) => {
              setTerm(e.target.value)
              setChosen(0)
            }}
          />
          <select
            className="form-control"
            id="admMergeTarget"
            style={{ marginTop: 8 }}
            size={6}
            value={chosen}
            onChange={(e) => setChosen(Number(e.target.value))}
          >
            {(found.data ?? []).map((ci) => (
              <option key={ci.id} value={ci.id}>
                {ci.name}
              </option>
            ))}
          </select>
        </div>
      </Modal>
    </Content>
  )
}
